import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNotificationContext } from '../contexts/NotificationContext';
import { useWebSocket } from '../contexts/WebSocketContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Sessions = () => {
  const { showSuccess, showError } = useNotificationContext();
  const [sessions, setSessions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    semester: 1,
    class: ''
  });
  const [formData, setFormData] = useState({
    course: '',
    teacher: '',
    class: '',
    room: '',
    type: 'LECTURE',
    dayOfWeek: 1,
    timeSlot: 1,
    semester: 1,
    group: ''
  });
  const [availableCourses, setAvailableCourses] = useState([]);
  const [tooltip, setTooltip] = useState({ show: false, content: '', x: 0, y: 0 });
  const [conflictAlert, setConflictAlert] = useState({ show: false, message: '' });
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, sessionId: null });
  const [draggedSession, setDraggedSession] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [academicYearConfig, setAcademicYearConfig] = useState(null);
  const [timeSlots, setTimeSlots] = useState([
    '8h30-10h00', '10h10-11h40', '11h50-13h20',
    '13h30-15h00', '15h10-16h40', '16h50-18h20'
  ]);
  const [days, setDays] = useState(['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']);

  const sessionTypes = {
    'LECTURE': 'Cours',
    'TUTORIAL': 'TD',
    'PRACTICAL': 'TP',
    'INTEGRATED': 'CI'
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (filters.department && filters.academicYear) {
      fetchClassesByDepartmentAndYear();
      fetchAcademicYearConfig();
    }
  }, [filters.department, filters.academicYear]);

  useEffect(() => {
    if (classes.length > 0) {
      fetchSessions();
    }
  }, [classes, filters.semester]);

  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'session_updated') {
      console.log('Session update reçu:', data.data?.action);
      if (data.data?.action === 'class_deleted') {
        console.log('🗑️ Classe supprimée, rafraîchissement des sessions');
        fetchClassesByDepartmentAndYear(); // Rafraîchir les classes
      }
      fetchSessions();
    }
  }, []);

  const { isConnected } = useWebSocket('sessions', handleWebSocketMessage);

  const fetchData = async () => {
    try {
      const [departmentsRes, academicYearsRes, coursesRes, teachersRes, roomsRes] = await Promise.all([
        axios.get(`${API_URL}/api/departments`),
        axios.get(`${API_URL}/api/academic-years`),
        axios.get(`${API_URL}/api/courses`),
        axios.get(`${API_URL}/api/teachers`),
        axios.get(`${API_URL}/api/rooms`)
      ]);

      setDepartments(departmentsRes.data);
      setAcademicYears(academicYearsRes.data);
      setCourses(coursesRes.data);
      setTeachers(teachersRes.data);
      setRooms(roomsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  const fetchClassesByDepartmentAndYear = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.department) params.append('department', filters.department);
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      params.append('limit', '10000');
      params.append('_t', Date.now()); // Force refresh
      
      const url = `${API_URL}/api/classes?${params.toString()}`;
      console.log('🔄 Fetching classes from:', url);
      
      const response = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      console.log('✅ Classes fetched:', response.data.length);
      setClasses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
    }
  };

  const fetchAcademicYearConfig = async () => {
    try {
      if (!filters.academicYear) {
        console.log('❌ No academic year selected');
        return;
      }

      console.log('🔄 Fetching config for academic year:', filters.academicYear);
      const response = await axios.get(
        `${API_URL}/api/academic-year-config/${filters.academicYear}`,
        {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        }
      );

      console.log('📦 Full Response:', JSON.stringify(response.data, null, 2));
      const config = response.data.data || response.data;
      console.log('✅ Extracted Config:', JSON.stringify(config, null, 2));
      
      if (!config) {
        console.log('⚠️ Config is empty, using defaults');
        setAcademicYearConfig(null);
        return;
      }

      setAcademicYearConfig(config);
      console.log('✅ Set academicYearConfig state');

      // Build timeslots from config
      if (config.timeslots && config.timeslots.length > 0) {
        console.log('📋 Raw timeslots from config:', JSON.stringify(config.timeslots, null, 2));
        const slots = config.timeslots
          .sort((a, b) => a.order - b.order)
          .map(slot => {
            const formatted = `${slot.startTime}-${slot.endTime}`;
            console.log(`  ⏰ Slot ${slot.slotNumber}: ${formatted} (order: ${slot.order})`);
            return formatted;
          });
        console.log('✅ Built timeslots array:', slots);
        setTimeSlots(slots);
        console.log('✅ Set timeSlots state');
      } else {
        console.log('⚠️ No timeslots in config, using defaults');
      }

      // Build days from config - FILTER BY isWorking
      if (config.daysOfWeek && config.daysOfWeek.length > 0) {
        console.log('📋 Raw days from config:', JSON.stringify(config.daysOfWeek, null, 2));
        const daysList = config.daysOfWeek
          .filter(day => day.isWorking === true) // ONLY include working days
          .sort((a, b) => a.order - b.order)
          .map(day => {
            console.log(`  📅 Day: ${day.dayName} (isWorking: ${day.isWorking}, order: ${day.order})`);
            return day.dayName;
          });
        console.log('✅ Built days array (filtered by isWorking):', daysList);
        setDays(daysList);
        console.log('✅ Set days state');
      } else {
        console.log('⚠️ No days in config, using defaults');
      }
    } catch (error) {
      console.error('❌ Error fetching academic year config:', error);
      console.log('⚠️ Using default timeslots and days');
      setAcademicYearConfig(null);
    }
  };

  const fetchSessions = async () => {
    try {
      const classIds = classes.map(cls => cls._id);
      const allSessions = [];

      for (const classId of classIds) {
        const response = await axios.get(`${API_URL}/api/sessions?class=${classId}&semester=${filters.semester}&limit=10000&_t=${Date.now()}`);
        const sessionsData = Array.isArray(response.data) ? response.data : response.data.data || [];
        allSessions.push(...sessionsData);
      }

      setSessions(allSessions);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        room: formData.room || null
      };

      let response;
      if (editingSession) {
        response = await axios.put(`${API_URL}/api/sessions/${editingSession._id}`, submitData);
      } else {
        response = await axios.post(`${API_URL}/api/sessions`, submitData);
      }

      await fetchSessions();
      closeModal();
      showSuccess(editingSession?._id ? 'Séance modifiée avec succès' : 'Séance ajoutée avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || 'Erreur lors de la sauvegarde de la séance';

      if (errorMessage.includes('Conflit')) {
        setConflictAlert({ show: true, message: errorMessage });
      } else {
        showError(errorMessage);
      }
    }
  };

  const closeConflictAlert = () => {
    setConflictAlert({ show: false, message: '' });
  };

  const checkOccupiedRooms = async (dayOfWeek, timeSlot, semester, excludeSessionId = null) => {
    try {
      const params = new URLSearchParams({
        dayOfWeek: dayOfWeek.toString(),
        timeSlot: timeSlot.toString(),
        semester: semester.toString(),
        limit: '10000',
        _t: Date.now().toString()
      });
      if (filters.academicYear) params.append('academicYear', filters.academicYear);

      const response = await axios.get(`${API_URL}/api/sessions?${params}`);
      const sessionsData = Array.isArray(response.data) ? response.data : response.data.data || [];

      const occupiedRoomIds = sessionsData
        .filter(session => {
          if (excludeSessionId && session._id === excludeSessionId) {
            return false;
          }
          return session.dayOfWeek === dayOfWeek &&
            session.timeSlot === timeSlot &&
            session.semester === semester &&
            session.room && session.room._id;
        })
        .map(session => session.room._id)
        .filter((roomId, index, array) => array.indexOf(roomId) === index);

      console.log('Salles occupées pour', { dayOfWeek, timeSlot, semester }, ':', occupiedRoomIds);
      setOccupiedRooms(occupiedRoomIds);
    } catch (error) {
      console.error('Erreur lors de la vérification des salles:', error);
      setOccupiedRooms([]);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/sessions/${id}`);
      await fetchSessions();
      showSuccess('Séance supprimée avec succès');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showError('Erreur lors de la suppression de la séance');
      fetchSessions();
    }
  };

  const confirmDelete = (sessionId) => {
    setDeleteConfirm({ show: true, sessionId });
    closeModal();
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.sessionId) {
      await handleDelete(deleteConfirm.sessionId);
    }
    setDeleteConfirm({ show: false, sessionId: null });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, sessionId: null });
  };

  const openModal = async (session = null) => {
    setEditingSession(session && session._id ? session : null);

    let newFormData;

    if (session && session._id) {
      const selectedClass = classes.find(cls => cls._id === session.class._id);
      if (selectedClass) {
        await loadCoursesForClass(selectedClass);
        await loadTeachersForClass(selectedClass);
      }
      newFormData = {
        course: session.course?._id || '',
        teacher: session.teacher?._id || '',
        class: session.class?._id || '',
        room: session.room?._id || '',
        type: session.type || 'LECTURE',
        dayOfWeek: session.dayOfWeek || 1,
        timeSlot: session.timeSlot || 1,
        semester: session.semester || filters.semester,
        group: session.group || ''
      };
    } else if (session && session.class) {
      await loadCoursesForClass(session.class);
      await loadTeachersForClass(session.class);
      newFormData = {
        course: '',
        teacher: '',
        class: session.class._id,
        room: '',
        type: 'PRACTICAL',
        dayOfWeek: session.dayOfWeek,
        timeSlot: session.timeSlot,
        semester: session.semester,
        group: session.group || ''
      };
    } else {
      setAvailableTeachers(teachers); // Reset to all teachers for new session
      newFormData = {
        course: '',
        teacher: '',
        class: '',
        room: '',
        type: 'LECTURE',
        dayOfWeek: 1,
        timeSlot: 1,
        semester: filters.semester,
        group: ''
      };
    }

    setFormData(newFormData);

    if (newFormData.dayOfWeek && newFormData.timeSlot && newFormData.semester) {
      const excludeId = editingSession?._id || null;
      await checkOccupiedRooms(newFormData.dayOfWeek, newFormData.timeSlot, newFormData.semester, excludeId);
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setOccupiedRooms([]);
    setAvailableCourses([]);
    setAvailableTeachers([]);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...formData,
      [name]: ['dayOfWeek', 'timeSlot', 'semester'].includes(name) ? Number(value) : value
    };

    setFormData(newFormData);

    if (name === 'class' && value) {
      const selectedClass = classes.find(cls => cls._id === value);
      if (selectedClass) {
        await loadCoursesForClass(selectedClass);
        await loadTeachersForClass(selectedClass);
      }
    }

    if (['dayOfWeek', 'timeSlot', 'semester'].includes(name)) {
      const dayOfWeek = name === 'dayOfWeek' ? Number(value) : newFormData.dayOfWeek;
      const timeSlot = name === 'timeSlot' ? Number(value) : newFormData.timeSlot;
      const semester = name === 'semester' ? Number(value) : newFormData.semester;

      if (dayOfWeek && timeSlot && semester) {
        const excludeId = editingSession?._id || null;
        await checkOccupiedRooms(dayOfWeek, timeSlot, semester, excludeId);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: name === 'semester' ? Number(value) : value
    });
  };

  const loadCoursesForClass = async (selectedClass) => {
    try {
      if (selectedClass.department && selectedClass.track) {
        const response = await axios.get(`${API_URL}/api/courses?department=${selectedClass.department._id}&track=${selectedClass.track._id}&semester=${filters.semester}&level=${selectedClass.level}&_t=${Date.now()}`);
        setAvailableCourses(response.data);
      } else {
        setAvailableCourses([]);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des cours:', error);
      setAvailableCourses([]);
    }
  };

  const loadTeachersForClass = async (selectedClass) => {
    try {
      if (selectedClass.department) {
        const response = await axios.get(`${API_URL}/api/teachers?department=${selectedClass.department._id}`);
        setAvailableTeachers(response.data);
      } else {
        setAvailableTeachers(teachers); // Fallback to all teachers
      }
    } catch (error) {
      console.error('Erreur lors du chargement des enseignants:', error);
      setAvailableTeachers(teachers); // Fallback to all teachers
    }
  };

  const getSessionsForClassAndSlot = (classId, dayIndex, timeSlotIndex) => {
    return sessions.filter(session =>
      session.class._id === classId &&
      session.dayOfWeek === dayIndex + 1 &&
      session.timeSlot === timeSlotIndex + 1
    );
  };



  const showTooltip = (e, session) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const tooltipContent = (
      <div className="p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
        <div><strong>Matière:</strong> {session.course?.name || 'N/A'} ({session.course?.code || 'N/A'})</div>
        <div><strong>Classe:</strong> {session.class?.name || 'N/A'}</div>
        <div><strong>Enseignant:</strong> {session.teacher ? `${session.teacher.firstName} ${session.teacher.lastName}` : 'N/A'}</div>
        <div><strong>Salle:</strong> {session.room?.name || 'N/A'}</div>
        <div><strong>Type:</strong> {sessionTypes[session.type] || session.type}</div>
        {session.group && <div><strong>Groupe:</strong> {session.group}</div>}
      </div>
    );

    setTooltip({
      show: true,
      content: tooltipContent,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const hideTooltip = () => {
    setTooltip({ show: false, content: '', x: 0, y: 0 });
  };

  const handleDragStart = (e, session) => {
    setDraggedSession(session);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, classId, dayIndex, timeIndex) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlot({ classId, dayIndex, timeIndex });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e, classId, dayIndex, timeIndex) => {
    e.preventDefault();
    setDragOverSlot(null);
    
    if (!draggedSession) return;
    
    const newDayOfWeek = dayIndex + 1;
    const newTimeSlot = timeIndex + 1;
    
    if (draggedSession.class._id === classId && 
        draggedSession.dayOfWeek === newDayOfWeek && 
        draggedSession.timeSlot === newTimeSlot) {
      setDraggedSession(null);
      return;
    }
    
    try {
      await axios.put(`${API_URL}/api/sessions/${draggedSession._id}`, {
        ...draggedSession,
        class: classId,
        dayOfWeek: newDayOfWeek,
        timeSlot: newTimeSlot
      });
      
      await fetchSessions();
      showSuccess('Séance déplacée avec succès');
    } catch (error) {
      console.error('Erreur lors du déplacement:', error);
      showError('Erreur lors du déplacement de la séance');
    }
    
    setDraggedSession(null);
  };

  const validateScheduleCompliance = async () => {
    try {
      const classId = filters.class || 'all';
      const timestamp = Date.now();
      const response = await axios.get(`${API_URL}/api/sessions/validate-compliance?classId=${classId}&semester=${filters.semester}&_t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setValidationResults(response.data);
      setShowValidation(true);
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      showError('Erreur lors de la vérification de conformité');
    }
  };

  const renderSessionCell = (sessions) => {
    if (!sessions || sessions.length === 0) return null;

    // Si 2 séances, affichage côte à côte
    if (sessions.length === 2) {
      return (
        <div className="flex gap-1 h-full">
          {sessions.map((session, index) => (
            <div
              key={session._id}
              draggable
              className={`w-1/2 p-1 rounded text-xs cursor-move transition-colors relative hover:opacity-80 ${
                session.type === 'LECTURE' ? 'bg-blue-100 text-blue-800' :
                session.type === 'TUTORIAL' ? 'bg-green-100 text-green-800' :
                'bg-purple-100 text-purple-800'
              } ${draggedSession?._id === session._id ? 'opacity-50' : ''}`}
              onDragStart={(e) => handleDragStart(e, session)}
              onMouseEnter={(e) => showTooltip(e, session)}
              onMouseLeave={hideTooltip}
              onClick={() => openModal(session)}
            >
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -translate-y-1/4 w-3 h-3 bg-gray-600 text-white rounded-full flex items-center justify-center font-bold" style={{fontSize: '8px'}}>
                G{index + 1}
              </div>
              <div className="font-medium truncate" style={{fontSize: '10px'}}>{session.course.code}</div>
              <div className="opacity-75 truncate" style={{fontSize: '9px'}}>{session.teacher.firstName.charAt(0)}. {session.teacher.lastName}</div>
            </div>
          ))}
        </div>
      );
    }

    // Si 1 séance, affichage normal
    return (
      <div className="h-full">
        {sessions.map((session) => (
          <div
            key={session._id}
            draggable
            className={`p-2 rounded text-xs cursor-move transition-colors h-full hover:opacity-80 ${
              session.type === 'LECTURE' ? 'bg-blue-100 text-blue-800' :
              session.type === 'TUTORIAL' ? 'bg-green-100 text-green-800' :
              session.type === 'INTEGRATED' ? 'bg-orange-100 text-orange-800' :
              'bg-purple-100 text-purple-800'
            } ${draggedSession?._id === session._id ? 'opacity-50' : ''}`}
            onDragStart={(e) => handleDragStart(e, session)}
            onMouseEnter={(e) => showTooltip(e, session)}
            onMouseLeave={hideTooltip}
            onClick={() => openModal(session)}
          >
            <div className="font-medium">{session.course.code || session.course.name}</div>
            <div className="opacity-75">{session.teacher.firstName.charAt(0)}. {session.teacher.lastName}</div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Séances</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={validateScheduleCompliance}
              className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 focus:ring-2 focus:ring-blue-500"
              disabled={classes.length === 0}
            >
              🔍 Vérifier Conformité
            </button>
            <button
              onClick={() => openModal()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
              disabled={classes.length === 0}
            >
              + Ajouter Séance
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
              <select
                name="department"
                value={filters.department}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner un département</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>{dept.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
              <select
                name="academicYear"
                value={filters.academicYear}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Sélectionner une année</option>
                {academicYears.map(year => (
                  <option key={year._id} value={year._id}>{year.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
              <select
                name="semester"
                value={filters.semester}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {[1, 2].map(sem => (
                  <option key={sem} value={sem}>Semestre {sem}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
              <select
                name="class"
                value={filters.class}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Toutes les classes</option>
                {classes.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {classes.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="border-collapse" style={{tableLayout: 'fixed', width: 'auto'}}>
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 sticky left-0 bg-gray-50 border border-gray-300" style={{minWidth: 'auto', wordWrap: 'break-word', whiteSpace: 'normal'}}>Classe</th>
                    {days.map((day, idx) => (
                      <th key={idx} className="px-3 py-2 text-center text-xs font-semibold text-gray-700 border border-gray-300" style={{width: '180px', minWidth: '180px', maxWidth: '180px'}}>{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {classes.filter(cls => !filters.class || cls._id === filters.class).map(cls => (
                    <tr key={cls._id} className="border-b border-gray-300">
                      <td className="px-3 py-2 text-sm font-semibold text-gray-900 sticky left-0 bg-gray-50 border border-gray-300" style={{minWidth: 'auto', wordWrap: 'break-word', whiteSpace: 'normal'}}>
                        {cls.name}
                      </td>
                      {days.map((day, dayIndex) => (
                        <td key={dayIndex} className="p-1 border border-gray-300 align-top bg-white" style={{width: '180px', minWidth: '180px', maxWidth: '180px'}}>
                          <div className="space-y-1" style={{minHeight: '300px'}}>
                            {timeSlots.map((timeSlot, timeIndex) => {
                              const sessionsForSlot = getSessionsForClassAndSlot(cls._id, dayIndex, timeIndex);
                              const hasMaxGroups = sessionsForSlot.length >= 2;

                              return (
                                <div
                                  key={timeIndex}
                                  className={`border rounded p-1 transition-colors flex flex-col ${
                                    dragOverSlot?.classId === cls._id && 
                                    dragOverSlot?.dayIndex === dayIndex && 
                                    dragOverSlot?.timeIndex === timeIndex
                                      ? 'border-primary-400 bg-primary-50' 
                                      : 'border-gray-200 bg-white'
                                  }`}
                                  style={{height: '75px', display: 'flex', flexDirection: 'column'}}
                                  onDragOver={(e) => handleDragOver(e, cls._id, dayIndex, timeIndex)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, cls._id, dayIndex, timeIndex)}
                                >
                                  <div className="flex justify-between items-start gap-1" style={{height: '16px', lineHeight: '16px', flexShrink: 0}}>
                                    <div className="text-xs text-gray-500 font-semibold">
                                      {timeSlot}
                                    </div>
                                    {!hasMaxGroups && (
                                      <button
                                        className="border border-dashed border-gray-300 rounded text-gray-400 hover:border-primary-300 hover:text-primary-600 transition-colors text-xs flex-shrink-0"
                                        style={{width: '16px', height: '16px', padding: '0', fontSize: '12px', lineHeight: '1'}}
                                        onClick={() => openModal({
                                          class: cls,
                                          dayOfWeek: dayIndex + 1,
                                          timeSlot: timeIndex + 1,
                                          semester: filters.semester,
                                          group: sessionsForSlot.length === 1 ? "Groupe 2" : ""
                                        })}
                                      >
                                        +
                                      </button>
                                    )}
                                  </div>
                                  <div className="flex-1 overflow-hidden flex items-center">
                                    {renderSessionCell(sessionsForSlot)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sessions.filter(session => !session.room && (!filters.class || session.class._id === filters.class)).length > 0 && (
              <div className="border-t p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Séances sans salle assignée</h3>
                <div className="flex flex-wrap gap-2">
                  {sessions.filter(session => !session.room && (!filters.class || session.class._id === filters.class)).map(session => (
                    <div
                      key={session._id}
                      className={`px-3 py-2 rounded text-xs cursor-pointer ${
                        session.type === 'LECTURE' ? 'bg-blue-100 text-blue-800' :
                        session.type === 'TUTORIAL' ? 'bg-green-100 text-green-800' :
                        session.type === 'INTEGRATED' ? 'bg-orange-100 text-orange-800' :
                        'bg-purple-100 text-purple-800'
                      }`}
                      onMouseEnter={(e) => showTooltip(e, session)}
                      onMouseLeave={hideTooltip}
                      onClick={() => openModal(session)}
                      draggable
                      onDragStart={(e) => handleDragStart(e, session)}
                    >
                      {session.class.name} - {(session.course?.code || 'N/A').substring(0, 6)} - {days[session.dayOfWeek - 1]} {timeSlots[session.timeSlot - 1]}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune classe trouvée</h3>
            <p className="text-gray-600">Veuillez sélectionner un département et une année universitaire pour afficher les séances.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingSession?._id ? 'Modifier Séance' : 'Ajouter Séance'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cours</label>
                    <select
                      name="course"
                      value={formData.course}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                      disabled={!formData.class}
                    >
                      <option value="">Sélectionner un cours</option>
                      {availableCourses.map(course => (
                        <option key={course._id} value={course._id}>{course.name} ({course.code})</option>
                      ))}
                    </select>
                    {!formData.class && (
                      <p className="text-xs text-gray-500 mt-1">Sélectionnez d'abord une classe</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Enseignant</label>
                    <select
                      name="teacher"
                      value={formData.teacher}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner un enseignant</option>
                      {(availableTeachers.length > 0 ? availableTeachers : teachers).map(teacher => (
                        <option key={teacher._id} value={teacher._id}>
                          {teacher.firstName} {teacher.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
                    <select
                      name="class"
                      value={formData.class}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner une classe</option>
                      {classes.map(cls => (
                        <option key={cls._id} value={cls._id}>{cls.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salle</label>
                    <select
                      name="room"
                      value={formData.room}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Sans salle</option>
                      {rooms.map(room => {
                        const isOccupied = occupiedRooms.includes(room._id);
                        return (
                          <option
                            key={room._id}
                            value={room._id}
                            className={isOccupied ? 'text-red-600' : 'text-green-600'}
                          >
                            {isOccupied ? '🔴' : '✅'} {room.name} ({room.capacity} places) {isOccupied ? '- OCCUPÉE' : '- LIBRE'}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="LECTURE">Cours</option>
                      <option value="TUTORIAL">TD</option>
                      <option value="PRACTICAL">TP</option>
                      <option value="INTEGRATED">Cours Intégré (CI)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Jour</label>
                    <select
                      name="dayOfWeek"
                      value={formData.dayOfWeek}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {days.map((day, index) => (
                        <option key={index} value={index + 1}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Horaire</label>
                    <select
                      name="timeSlot"
                      value={formData.timeSlot}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {timeSlots.map((slot, index) => (
                        <option key={index} value={index + 1}>{slot}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Semestre</label>
                    <select
                      name="semester"
                      value={formData.semester}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      {[1, 2].map(sem => (
                        <option key={sem} value={sem}>Semestre {sem}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Groupe {formData.type === 'PRACTICAL' ? '(requis pour TP)' : '(optionnel)'}
                  </label>
                  {formData.type === 'PRACTICAL' ? (
                    <select
                      name="group"
                      value={formData.group}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner un groupe</option>
                      <option value="Groupe 1">Groupe 1</option>
                      <option value="Groupe 2">Groupe 2</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="group"
                      value={formData.group}
                      onChange={handleChange}
                      placeholder="Ex: Groupe 1"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  )}
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingSession?._id ? 'Modifier' : 'Ajouter'}
                  </button>
                  {editingSession?._id && (
                    <button
                      type="button"
                      onClick={() => confirmDelete(editingSession._id)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500"
                    >
                      Supprimer
                    </button>
                  )}
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {tooltip.show && (
          <div
            className="fixed z-50 pointer-events-none"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateX(-50%) translateY(-100%)'
            }}
          >
            {tooltip.content}
          </div>
        )}

        {conflictAlert.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">⚠️ Conflit détecté</h3>
                <button onClick={closeConflictAlert} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <div className="text-sm text-gray-700">
                  {conflictAlert.message.split('\n').map((line, index) => (
                    <div key={index} className={line.startsWith('•') ? 'ml-4 text-red-600' : 'font-medium'}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-red-600">🗑️ Confirmer la suppression</h3>
                <button onClick={cancelDelete} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 mb-4">Êtes-vous sûr de vouloir supprimer cette séance ? Cette action est irréversible.</p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={cancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showValidation && validationResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className={`text-lg font-medium ${
                  validationResults.isCompliant ? 'text-green-600' : 'text-red-600'
                }`}>
                  {validationResults.isCompliant ? '✅ Emploi du temps conforme' : '⚠️ Non-conformités détectées'}
                </h3>
                <button onClick={() => setShowValidation(false)} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <div className="p-4">
                {/* Résumé des heures par classe */}
                {validationResults.classSummaries && validationResults.classSummaries.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-md font-medium text-gray-900 mb-3">📈 Résumé des heures par classe</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Classe</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Étudiants</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Cours</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">TD</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">TP</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Total</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Statut</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {validationResults.classSummaries.map((summary, index) => (
                            <tr key={index} className={summary.isCompliant ? 'bg-green-50' : 'bg-red-50'}>
                              <td className="px-3 py-2 font-medium">{summary.className}</td>
                              <td className="px-3 py-2 text-center">{summary.students}</td>
                              <td className="px-3 py-2 text-center">
                                <div className="text-xs">
                                  <div className="text-blue-600">{summary.actualHours.lectures}h</div>
                                  <div className="text-gray-500">({summary.expectedHours.lectures}h)</div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="text-xs">
                                  <div className="text-green-600">{summary.actualHours.tutorials}h</div>
                                  <div className="text-gray-500">({summary.expectedHours.tutorials}h)</div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="text-xs">
                                  <div className="text-purple-600">{summary.actualHours.practicals}h</div>
                                  <div className="text-gray-500">({summary.expectedHours.practicals}h)</div>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center font-medium">
                                <div className={`text-sm ${
                                  summary.differences.total === 0 ? 'text-green-600' : 
                                  summary.differences.total > 0 ? 'text-blue-600' : 'text-red-600'
                                }`}>
                                  {summary.actualHours.total}h
                                </div>
                                <div className="text-xs text-gray-500">({summary.expectedHours.total}h)</div>
                                {summary.differences.total !== 0 && (
                                  <div className={`text-xs font-medium ${
                                    summary.differences.total > 0 ? 'text-blue-600' : 'text-red-600'
                                  }`}>
                                    {summary.differences.total > 0 ? '+' : ''}{summary.differences.total}h
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {summary.isCompliant ? (
                                  <span className="text-green-600 font-medium">✓</span>
                                ) : (
                                  <span className="text-red-600 font-medium">✗</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <p><strong>Format:</strong> Réel (Attendu) | <strong>Légende:</strong> Cours (bleu), TD (vert), TP (violet)</p>
                    </div>
                  </div>
                )}
                
                {/* Violations */}
                {validationResults.isCompliant ? (
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">🎉</div>
                    <p className="text-lg text-green-600 font-medium">L'emploi du temps respecte toutes les contraintes !</p>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-800">
                        <strong>{validationResults.totalViolations}</strong> violation(s) détectée(s)
                      </p>
                    </div>
                    <div className="space-y-3">
                      {validationResults.violations.map((violation, index) => (
                        <div key={index} className="p-3 border border-red-200 rounded-lg bg-red-50">
                          <div className="font-medium text-red-800">{violation.course}</div>
                          <div className="text-sm text-red-600">{violation.message}</div>
                          <div className="text-xs text-red-500 mt-1">
                            Type: {violation.type.replace('_', ' ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={() => setShowValidation(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sessions;